// Shared rules for the /[lang]/wine-tours landing pages. Framework-free so the
// build-time prefetch (list-prefetch.ts), generateStaticParams and the screen
// all apply the same threshold.

import { cities } from '@/lib/events-data';
import { WINE_THEME } from '@todo/core/list-queries';
import { tourIsPrivate, type Tour } from '@todo/core/tours';

/**
 * A departure city gets its own /wine-tours/[city] page only with at least this
 * many wine tours — below it the page would be thin content competing with the
 * regional hub for the same queries. The same floor makes a page `noindex`
 * if it ever renders with fewer (e.g. on native, or after the catalogue
 * shrinks between builds).
 */
export const MIN_WINE_TOURS_PER_PAGE = 3;

/** City ids (in `cities` order) with enough wine tours for their own page. */
export function wineTourCityIds(tours: Pick<Tour, 'city_id'>[]): string[] {
  const counts = new Map<string, number>();
  for (const tour of tours) {
    if (tour.city_id) counts.set(tour.city_id, (counts.get(tour.city_id) ?? 0) + 1);
  }
  return cities
    .map((city) => city.id)
    .filter((id) => (counts.get(id) ?? 0) >= MIN_WINE_TOURS_PER_PAGE);
}

/**
 * Rows in a wine page's comparison table — its top tours in the page's own
 * (tours_by_theme) order. Past this a table stops being scannable; the cards
 * below still list every tour.
 */
export const WINE_COMPARE_LIMIT = 20;

/**
 * A wine page's tours split by Viator's PRIVATE_TOUR flag, each half keeping
 * the page's order: "private wine tour {city}" is its own search, so private
 * tours get their own block (and heading) instead of mixing into the grid.
 */
export function splitWineTours(tours: Tour[]): { group: Tour[]; private: Tour[] } {
  const group: Tour[] = [];
  const priv: Tour[] = [];
  for (const tour of tours) (tourIsPrivate(tour) ? priv : group).push(tour);
  return { group, private: priv };
}

/** Whether the tour_themes() classifier tagged this tour as a wine tour. */
export function isWineTour(tour: Pick<Tour, 'themes'>): boolean {
  return tour.themes?.includes(WINE_THEME) ?? false;
}
